import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheckCircle,
  faEnvelope,
  faExclamationCircle,
  faInbox,
  faKey,
  faPaperPlane,
  faSignInAlt,
} from '@fortawesome/free-solid-svg-icons';
import useUser from '../hooks/useUser';
import { Alert, Button, PageShell, Panel, Spinner } from '../design-system';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { buildAuthOverlayRouteState } from '../utils/authOverlayNavigation';
import '../components/Auth/AuthSurface.css';

type EmailClient = 'outlook' | 'apple-mail' | 'gmail';

type UseUserResult = {
  isAuthenticated: boolean;
  getApiToken: () => Promise<string>;
};

const detectEmailClient = (): EmailClient => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('windows')) {
    return 'outlook';
  }

  if (userAgent.includes('macintosh')) {
    return 'apple-mail';
  }

  return 'gmail';
};

const emailClientUrl: Record<EmailClient, string> = {
  outlook: 'https://outlook.live.com',
  'apple-mail': 'https://www.icloud.com/mail',
  gmail: 'https://mail.google.com',
};

const errorMessage = (error: unknown): string => (
  error instanceof Error ? error.message : 'Unknown error'
);

export default function SetPasswordPage(): React.ReactElement {
  const { isAuthenticated, getApiToken } = useUser() as UseUserResult;
  const navigate = useNavigate();
  const location = useLocation();
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailClient, setEmailClient] = useState<EmailClient>('gmail');
  const didSend = useRef(false);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const email = params.get('email');
  const redirectTo = params.get('redirectTo');
  const customerId = params.get('customerId');

  useEffect(() => {
    setEmailClient(detectEmailClient());

    document.body.classList.add('set-password-page-body');
    return () => {
      document.body.classList.remove('set-password-page-body');
    };
  }, []);

  useEffect(() => {
    const updatePasswordStatus = async () => {
      if (!isAuthenticated) return;

      try {
        const token = await getApiToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/password-status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to update password status');
        }

        const data = await response.json();

        if (data.success) {
          pushEvent({
            event: ANALYTICS_EVENTS.PASSWORD_SET,
            userEmail: email,
            source: 'set_password_page',
            customerId,
          });

          if (redirectTo) {
            navigate(`/${redirectTo}?email=${encodeURIComponent(email || '')}&customerId=${encodeURIComponent(customerId || '')}`);
          } else {
            navigate('/');
          }
        }
      } catch (updateError) {
        console.error('Error updating password status:', updateError);
        navigate(redirectTo ? `/${redirectTo}` : '/');
      }
    };

    if (isAuthenticated) {
      updatePasswordStatus();
    }
  }, [isAuthenticated, getApiToken, navigate, email, redirectTo, customerId]);

  const sendResetEmail = async () => {
    if (!email) {
      setError('No email provided.');
      return;
    }

    if (didSend.current && loading) {
      return;
    }

    didSend.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/send-reset-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (response.ok) {
        pushEvent({
          event: ANALYTICS_EVENTS.PASSWORD_RESET_EMAIL_SENT,
          userEmail: email,
          success: true,
          customerId,
        });
        setEmailSent(true);
      } else {
        pushEvent({
          event: ANALYTICS_EVENTS.PASSWORD_RESET_EMAIL_ERROR,
          errorType: 'api_error',
          userEmail: email,
          errorMessage: data.error,
          customerId,
        });
        setError(data.error || 'Failed to send reset email.');
      }
    } catch (sendError) {
      pushEvent({
        event: ANALYTICS_EVENTS.PASSWORD_RESET_EMAIL_ERROR,
        errorType: 'network_error',
        userEmail: email,
        errorMessage: errorMessage(sendError),
        customerId,
      });
      setError('Failed to send reset email.');
    } finally {
      setLoading(false);
      didSend.current = false;
    }
  };

  useEffect(() => {
    if (!email) {
      setError('No email provided. Please contact support or check your URL parameters.');
      pushEvent({
        event: ANALYTICS_EVENTS.SET_PASSWORD_PAGE_ERROR_NO_EMAIL,
        source: location.pathname,
        customerId,
      });
      return;
    }

    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page_title: 'Set Password',
      userEmail: email,
      source: location.pathname,
      customerId,
    });

    setEmailSent(true);
  }, [email, customerId, location.pathname]);

  const handleOpenEmail = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.SET_PASSWORD_EMAIL_CLIENT_OPENED,
      userEmail: email,
      emailClient,
      customerId,
    });

    window.open(emailClientUrl[emailClient], '_blank');
  };

  const handleLogin = () => {
    const loginParams = new URLSearchParams({
      returnTo: redirectTo
        ? `/${redirectTo.replace(/^\/+/, '')}?email=${encodeURIComponent(email || '')}&customerId=${encodeURIComponent(customerId || '')}`
        : '/',
      source: 'set_password_page',
    });

    if (email) {
      loginParams.set('login_hint', email);
    }

    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'unified_login',
      source: 'set_password_page',
      userEmail: email,
      customerId,
    });

    navigate(`/login?${loginParams.toString()}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  return (
    <PageShell className="auth-page" innerClassName="auth-page__inner" aria-label="Set password">
      <div className="auth-status-layout">
        <Panel className="auth-status-panel">
          <div className="auth-status-copy">
            <span className="auth-status-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faKey} />
            </span>
            <h1>Set Your Password</h1>
            <p>One last step to secure your account</p>
          </div>

          {!email ? (
            <>
              <Alert role="alert" variant="danger">
                <div className="auth-status-list">
                  <strong>
                    <FontAwesomeIcon icon={faExclamationCircle} /> No email address found
                  </strong>
                  <span>This could be due to:</span>
                  <span>An incomplete link</span>
                  <span>An expired session</span>
                </div>
              </Alert>
              <Button as={Link} fullWidth to="/checkout" variant="primary">
                Return to Checkout
              </Button>
            </>
          ) : (
            <>
              <div className="auth-status-copy">
                <span className="auth-status-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faPaperPlane} />
                </span>
                <h2>Check Your Email</h2>
                <p>
                  We&apos;ve sent password setup instructions to:
                  <br />
                  <span className="auth-status-email">{email}</span>
                </p>
              </div>

              <div className="auth-status-list">
                <span><FontAwesomeIcon icon={faInbox} /> Open the email from BotSpot</span>
                <span><FontAwesomeIcon icon={faArrowRight} /> Click the &quot;Set Password&quot; button</span>
                <span><FontAwesomeIcon icon={faCheckCircle} /> Create your secure password</span>
              </div>

              {loading ? (
                <div className="auth-loading-note">
                  <Spinner label="Sending email" />
                  <span>Sending email...</span>
                </div>
              ) : (
                <div className="auth-status-actions">
                  <Button icon={<FontAwesomeIcon icon={faEnvelope} />} onClick={handleOpenEmail} variant="primary">
                    Open Email App
                  </Button>
                  <Button icon={<FontAwesomeIcon icon={faSignInAlt} />} onClick={handleLogin} variant="outline">
                    Continue to Login
                  </Button>
                  <Button disabled={!emailSent} onClick={sendResetEmail} variant="secondary">
                    Send password email again
                  </Button>
                </div>
              )}

              <p className="auth-fine-print">
                Can&apos;t find the email? Check your spam folder or send the password email again.
              </p>
            </>
          )}

          {error && (
            <Alert role="alert" variant="danger">
              {error}
            </Alert>
          )}
        </Panel>
      </div>
    </PageShell>
  );
}
